import axios, { AxiosError } from 'axios';
import retry from 'axios-retry';
import Qs from 'qs';
import { getCurrentLanguageFromStorage } from 'i18n-config';
import { languageLocalStorage } from 'storage/i18n';
import * as auth from 'utility/Authentication';
import store from 'store';
import {
  showPaymentMessage,
  showTimeoutMessage,
} from 'store/authentication/authenticationAction';
import { postSessionTimeoutMessage } from 'store/authentication/channels';

export const NOBUSINESS_ERROR_CODE = 'NOBUSINESS';

export const AUTHORIZED_EMBED_IFRAME_PATH = '/embed';

export const DefaultAxiosRetryConfig = {
  retries: 2,
  retryCondition: (error) => {
    return error.response?.status >= 500;
  },
};

// These paths are navigable when the current business is inactive
const allowedInactivePaths = [
  '/inactive-business',
  '/login',
  '/choose-business',
  '/payment-required',
];

const isSubscriptionError = (response) =>
  response.data?.errors?.code === 'subscription_error';

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_PATH,
  withCredentials: true,
  timeout: 120000, // 2 minutes
});

retry(instance, {
  retries: 0,
});

instance.interceptors.request.use((config) => {
  if (config.baseURL !== import.meta.env.VITE_API_BASE_PATH) {
    return config;
  }

  const isAuthorizedEmbedIframe = window.location.pathname.startsWith(
    AUTHORIZED_EMBED_IFRAME_PATH
  );

  if (
    (window.location === window.parent.location || isAuthorizedEmbedIframe) &&
    auth.status.authorized
  ) {
    const businessId = auth.status.businessId;
    if (businessId && businessId !== 'null') {
      config.headers[`x-business-id`] = businessId;
    }
  }

  config.headers['accept-language'] =
    languageLocalStorage.val || getCurrentLanguageFromStorage();
  return config;
});

// errors shim
instance.interceptors.response.use(
  (response) => response,
  (err) => {
    if (err.response?.data && typeof err.response.data !== 'string') {
      err.response.data.errors = { ...err.response.data };
    }
    return Promise.reject(err);
  }
);

instance.interceptors.response.use((response) => {
  if (
    response.config.url === '/login' &&
    response.status === 200 &&
    !response.data.has_business
  ) {
    throw new AxiosError(
      'No Business Associated',
      response.config,
      NOBUSINESS_ERROR_CODE,
      response.request,
      response
    );
  }
  return response;
});

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response } = error;

    if (!response) {
      throw error;
    }

    const isApiRequest =
      response.config.baseURL === import.meta.env.VITE_API_BASE_PATH;

    const isAuthenticationError =
      response.status === 401 ||
      response.status === 403 ||
      response.status === 402;

    // 403 error from v2 (hapi auth scope error) that represents only that the user hasn't yet
    // selected a business, but tried to do something business-specific.
    // The user can recover by selecting a business; the app _should_ be
    // in a state such that our auth routing will send the user to the
    // choose business page i.e. the user is logged in, but no business data in our store

    const isInactiveBusiness =
      isApiRequest && window.location.href.endsWith('inactive-business');

    const isAppPermissionsError =
      response.status === 403 &&
      response.data &&
      String(response.data?.code).includes('permission_denied');

    if (
      isApiRequest &&
      isAuthenticationError &&
      !isAppPermissionsError &&
      error.code !== NOBUSINESS_ERROR_CODE
    ) {
      // we used to skip the logout API call, as making it would trigger
      // now the logout traps it
      // an infinite loop (call -> 401 --> intercept --> call)
      if (response.status === 403 && isSubscriptionError(response)) {
        // redirect if not already on the inactive business page
        if (
          !allowedInactivePaths.some((path) =>
            window.location.href.endsWith(path)
          )
        ) {
          const url = `${window.location.protocol}//${window.location.host}/inactive-business`;
          window.location.href = url;
        }
      } else if (response.status === 401) {
        const state = store.getState();
        const { authentication } = state;
        // we want a 401 to trigger a logout only if we are logged in
        if (
          authentication.user &&
          authentication.chosenBusiness &&
          !authentication.showBlocker
        ) {
          store.dispatch(showTimeoutMessage());

          postSessionTimeoutMessage(authentication.instanceId);
        }
      } else if (response.status === 402) {
        // The user has reached a limit of their account, and needs to be prompted
        // to upgrade
        store.dispatch(showPaymentMessage(response.data));
      }
    }

    if (isInactiveBusiness) {
      return;
    }

    throw error;
  }
);

// to support &option_ids=123&option_ids=456 instead of option_ids[]=123
// https://github.com/axios/axios/issues/604#issuecomment-321460450
instance.defaults.paramsSerializer = (params) => {
  return Qs.stringify(params, { arrayFormat: 'repeat' });
};

export default instance;

export const getOriginalError = (err) => err?.response?.data;
export const getOriginalErrorStatusCode = (err) => err?.response.status;
